import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import useDebounce from "../external/ensolvers-core-frontend-mui/hooks/useDebounce";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { trace } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import Position from "../models/Position";
import PositionService from "../services/PositionService";
import ProjectService from "../services/ProjectService";
import UserService from "../services/UserService";
import WorkLogService from "../services/WorkLogService";
import { BillingPolicy } from "../types/BillingPolicy";
import { Routes } from "../types/Routes";
import RoleUtils from "../utils/RoleUtils";
import useUpdateUrl from "./useUpdateUrl";

export enum PositionStatus {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

const usePositionList = () => {
  const history = useHistory();
  const [projectsNames, setProjectNames] = useState<AutoCompleteUIDTO[]>([]);
  const [userNames, setUserNames] = useState<AutoCompleteUIDTO[]>([]);
  const [canManageProjects, setCanManageProjects] = useState<boolean>(false);
  const [update, setUpdate] = useState<number | undefined>(undefined);
  const [selectedPosition, setSelectedPosition] = useState<Position | undefined>(undefined);
  const [newBillingPolicy, setNewBillingPolicy] = useState<boolean>(false);
  const [projectsAssigned, setProjectsAssigned] = useState<AutoCompleteUIDTO[]>([]);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [reloadProjects, setReloadProjects] = useState(false);
  const [searchRoleKey, setSearchRoleKey] = useState<AutoCompleteUIDTO>({ externalId: "All", label: "All" });
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasWorklogs, setHasWorklogs] = useState<boolean>(false);
  const [bonusValue, setBonusValue] = useState(0);
  const [isShadowChecked, setIsShadowChecked] = useState(false);
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [positionStatus, setPositionStatus] = useState<string>(PositionStatus.ACTIVE);
  const debounceAssigned = useDebounce(assigned, 500);
  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const role = searchParams.get("role");
  const assignedToMe = searchParams.get("assigned");
  const {updateUrl} = useUpdateUrl(Routes.POSITIONS, searchParams);

  useEffect(() => {
    getAllProjectsNames(assigned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadProjects, debounceAssigned]);

  useEffect(() => {
    if (project !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === project);
      if (projectFilter) setSelectedProject(projectFilter);
    } else if (storedProject !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) setSelectedProject(projectFilter);
      searchParams.set("project", storedProject.externalId);
    }

    if (role !== null) {
      setSearchRoleKey({ externalId: role, label: role });
    } else {
      searchParams.set("role", "All");
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsNames]);

  useEffect(() => {
    checkIfUserIsManagerFromProject();
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUpdate(Date.now());
  }, [positionStatus]);

  const getAllProjectsNames = (assigned: boolean) => {
    setIsLoading(true);
    ProjectService.findAllProjectsNames(false, assigned)
      .then((response) => setProjectNames(response.getContent()))
      .catch((error) => trace("Error getting project names", error))
      .finally(() => setIsLoading(false));

    UserService.findUserNames()
      .then((response) => {
        setUserNames(response.getContentOrThrowError());
      })
      .catch((error) => trace("Error getting user names.", error))
      .finally(() => setIsLoading(false));
  };

  const checkIfUserIsManagerFromProject = async () => {
    if (RoleUtils.isAdmin()) {
      setCanManageProjects(true);
    } else {
      if (selectedProject && selectedProject.externalId !== "" && !RoleUtils.isAdmin()) {
        const response = await ProjectService.isUserCoreManagerFromProject(selectedProject.externalId!, AuthService.getUser()?.externalId!);
        setCanManageProjects(response.succeeded());
      }
    }
  };

  const handleOnDeletePosition = async () => {
    if (!selectedPosition) return;

    try {
      await PositionService.delete(selectedPosition).then(() => NotificationService.info("Position deleted successfully!"));
      setSelectedPosition(undefined);
      setUpdate(Date.now());
    } catch (error) {
      NotificationService.error("Cannot delete because there is an associated assignment.");
    }
  };

  const handleOnChangeInputBillingPolicy = (event: any) => {
    const value = event.target.value;
    if (value === "SUM_HOURS_WITH_MINIMUM") {
      setNewBillingPolicy(true);
    } else {
      setNewBillingPolicy(false);
    }
    setBonusValue(0);
  };

  const handleChangeAssignedProjects = () => {
    setAssigned(!assigned);
    setSelectedProject(EMPTY_AUTOCOMPLETE);
    searchParams.delete("project");
    searchParams.set("assigned", (!assigned).toString());
    setStoredProject(EMPTY_AUTOCOMPLETE);
    updateUrl();
  };

  const onCreatePosition = (position: Position) => {
    if (selectedProject?.externalId) {
      position.projectExternalId = selectedProject.externalId;
      setNewBillingPolicy(false);
      return PositionService.create(position).then(() => NotificationService.info("New position created successfully!"));
    } else {
      NotificationService.error("You must select a project before creating a Position");
    }
  };

  const onUpdatePosition = async (position: Position): Promise<boolean> => {
    try {
      let worklogs = await WorkLogService.findByPositionExternalId(position.externalId!);
      setHasWorklogs(worklogs.getContent().length > 0);
      setNewBillingPolicy(position.billingPolicy === BillingPolicy.SUM_HOURS_WITH_MINIMUM);
      await PositionService.update(position);
      NotificationService.info("Position updated successfully!");
      return true;
    } catch (err: any) {
      NotificationService.error(err.response.data.message);
      return false;
    }
  };  

  const onEditPosition = async (position: Position) => {
    let worklogs = await WorkLogService.findByPositionExternalId(position.externalId!);
    setHasWorklogs(worklogs.getContent().length > 0);
    if (position.billingPolicy.toString() === "SUM_HOURS_WITH_MINIMUM") {
      setNewBillingPolicy(true);
    } else {
      setNewBillingPolicy(false);
    }
  };

  const onCancelDialog = async () => {
    setHasWorklogs(false);
    setBonusValue(0);
    setIsShadowChecked(false);
    setNewBillingPolicy(false);
    setIsEditing(false);
  };

  const buildBillingTypeText = (instance: Position) => {
    let text = BillingPolicy[instance.billingPolicy];
    if (instance.shadow) {
      text = text + ", Shadow";
    }
    if (instance.onDemand) {
      text = text + ", On Demand";
    }
    if (instance.bonus > 0) {
      text = text + `, Bonus ${instance.bonus}%`;
    }
    return text;
  };

  return {
    buildBillingTypeText,
    onCancelDialog,
    history,
    projectsNames,
    canManageProjects,
    update,
    selectedPosition,
    setSelectedPosition,
    newBillingPolicy,
    setNewBillingPolicy,
    projectsAssigned,
    assigned,
    searchRoleKey,
    isLoading,
    setSearchRoleKey,
    handleChangeAssignedProjects,
    handleOnChangeInputBillingPolicy,
    handleOnDeletePosition,
    onCreatePosition,
    onUpdatePosition,
    onEditPosition,
    isEditing,
    setIsEditing,
    hasWorklogs,
    userNames,
    isShadowChecked,
    bonusValue,
    setIsShadowChecked,
    setBonusValue,
    searchParams,
    updateUrl,
    selectedProject,
    setSelectedProject,
    positionStatus,
    setPositionStatus,
    storedProject,
    setStoredProject,
  };
};

export default usePositionList;
